// @tennis-matches/services/trainerstunden/trainer-club-memberships
// Vereinszuordnungen pro Trainer (Mehrfach + Hauptverein).

import { getSupabaseClient } from "../supabase/index.js";
import { loadVisibleTenantScopeIdsRemote } from "../governance/scopes.service.js";
import { resolveTenantScopeLabelsRemote } from "../governance/tenant-scope-labels.service.js";

function __asStr(v) {
  return String(v ?? "").trim();
}

/**
 * @typedef {Object} TrainerClubMembership
 * @property {string} trainerId
 * @property {string} tenantId
 * @property {boolean} isPrimary
 * @property {string} clubName
 * @property {string} city
 */

/**
 * @typedef {Object} AssignableClubOption
 * @property {string} tenantId
 * @property {string} clubName
 * @property {string} city
 * @property {string} displayLabel
 */

/**
 * @param {unknown} row
 * @returns {TrainerClubMembership|null}
 */
export function mapTrainerClubMembershipRow(row) {
  if (!row || typeof row !== "object") return null;
  const trainerId = __asStr(row.trainer_id);
  const tenantId = __asStr(row.tenant_id);
  if (!trainerId || !tenantId) return null;
  return {
    trainerId,
    tenantId,
    isPrimary: Boolean(row.is_primary),
    clubName: __asStr(row.club_name) || "Unbenannter Verein",
    city: __asStr(row.city),
  };
}

/**
 * @param {TrainerClubMembership[]} memberships
 * @returns {TrainerClubMembership[]}
 */
export function sortTrainerClubMemberships(memberships) {
  return [...(Array.isArray(memberships) ? memberships : [])].sort((a, b) => {
    if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
    const nameCmp = a.clubName.localeCompare(b.clubName, "de");
    if (nameCmp !== 0) return nameCmp;
    return a.tenantId.localeCompare(b.tenantId);
  });
}

/**
 * @param {string[]} selectedTenantIds
 * @param {string} primaryTenantId
 * @returns {Array<{ tenant_id: string, is_primary: boolean }>}
 */
export function buildTrainerClubMembershipsRpcPayload(selectedTenantIds, primaryTenantId) {
  const primary = __asStr(primaryTenantId);
  const ids = Array.from(new Set((Array.isArray(selectedTenantIds) ? selectedTenantIds : []).map(__asStr).filter(Boolean)));
  return ids.map((tenantId) => ({
    tenant_id: tenantId,
    is_primary: tenantId === primary,
  }));
}

/**
 * @param {string[]} selectedTenantIds
 * @param {string} primaryTenantId
 * @returns {{ ok: boolean, reason?: string }}
 */
export function validateTrainerClubMembershipSelection(selectedTenantIds, primaryTenantId) {
  const ids = Array.from(new Set((Array.isArray(selectedTenantIds) ? selectedTenantIds : []).map(__asStr).filter(Boolean)));
  const primary = __asStr(primaryTenantId);
  if (!ids.length) return { ok: false, reason: "membership-required" };
  if (!primary || !ids.includes(primary)) return { ok: false, reason: "primary-required" };
  return { ok: true };
}

/**
 * @param {string[]} trainerIds
 * @returns {Promise<{ ok: boolean, byTrainerId?: Map<string, TrainerClubMembership[]>, reason?: string, error?: unknown }>}
 */
export async function loadTrainerClubMembershipsByTrainerIds(trainerIds) {
  const ids = Array.from(new Set((Array.isArray(trainerIds) ? trainerIds : []).map(__asStr).filter(Boolean)));
  const byTrainerId = new Map();
  if (!ids.length) return { ok: true, byTrainerId };

  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase", byTrainerId };

  try {
    const { data, error } = await supabase.rpc("trainerstunden_list_trainer_club_memberships", {
      p_trainer_ids: ids,
    });
    if (error) {
      const code = __asStr(error?.code);
      if (code === "42883" || code === "PGRST202") return { ok: true, byTrainerId, reason: "rpc-missing" };
      return { ok: false, reason: "rpc-error", error, byTrainerId };
    }

    const payload = data && typeof data === "object" ? data : {};
    if (payload.ok === false) {
      return { ok: false, reason: __asStr(payload.reason) || "rpc-failed", byTrainerId };
    }

    const rows = Array.isArray(payload.memberships) ? payload.memberships : [];
    for (const row of rows) {
      const mapped = mapTrainerClubMembershipRow(row);
      if (!mapped) continue;
      const list = byTrainerId.get(mapped.trainerId) || [];
      list.push(mapped);
      byTrainerId.set(mapped.trainerId, list);
    }
    for (const [key, list] of byTrainerId.entries()) {
      byTrainerId.set(key, sortTrainerClubMemberships(list));
    }
    return { ok: true, byTrainerId };
  } catch (e) {
    return { ok: false, reason: "rpc-failed", error: e, byTrainerId };
  }
}

/**
 * @param {string} trainerId
 * @param {Array<{ tenant_id: string, is_primary: boolean }>} memberships
 */
export async function replaceTrainerClubMembershipsRemote(trainerId, memberships) {
  const tid = __asStr(trainerId);
  if (!tid) return { ok: false, reason: "missing-trainer-id" };

  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  try {
    const { data, error } = await supabase.rpc("trainerstunden_replace_trainer_club_memberships", {
      p_trainer_id: tid,
      p_memberships: Array.isArray(memberships) ? memberships : [],
    });
    if (error) {
      const code = __asStr(error?.code);
      if (code === "42883" || code === "PGRST202") return { ok: false, reason: "rpc-missing", error };
      return { ok: false, reason: "rpc-error", error };
    }
    const payload = data && typeof data === "object" ? data : {};
    if (payload.ok === false) {
      return { ok: false, reason: __asStr(payload.reason) || "save-failed", error: payload };
    }
    return { ok: true, primaryTenantId: __asStr(payload.primary_tenant_id), count: Number(payload.count) || 0 };
  } catch (e) {
    return { ok: false, reason: "rpc-failed", error: e };
  }
}

/**
 * @param {Array<{ tenantId?: string }>} [extraTenants]
 * @returns {Promise<{ ok: boolean, clubs: AssignableClubOption[], reason?: string }>}
 */
export async function loadAssignableClubsForTrainer(extraTenants = []) {
  const scopeRes = await loadVisibleTenantScopeIdsRemote();
  if (!scopeRes.ok) return { ok: false, clubs: [], reason: scopeRes.reason || "scope-load-failed" };

  const tenantIds = new Set((scopeRes.scopeIds || []).map(__asStr).filter(Boolean));
  for (const row of Array.isArray(extraTenants) ? extraTenants : []) {
    const tid = __asStr(row?.tenantId);
    if (tid) tenantIds.add(tid);
  }

  const ids = [...tenantIds];
  if (!ids.length) return { ok: true, clubs: [] };

  const labelsRes = await resolveTenantScopeLabelsRemote(ids);
  const byScope = labelsRes.ok ? labelsRes.byScopeId || {} : {};

  const clubs = ids
    .map((tenantId) => {
      const row = byScope[tenantId] || byScope[tenantId.toLowerCase()] || {};
      const clubName = __asStr(row.display_name) || "Unbenannter Verein";
      const city = __asStr(row.city);
      const displayLabel = __asStr(row.display_label) || (city ? `${clubName} (${city})` : clubName);
      return { tenantId, clubName, city, displayLabel };
    })
    .sort((a, b) => a.displayLabel.localeCompare(b.displayLabel, "de"));

  return { ok: true, clubs };
}

/**
 * @param {string} reason
 * @returns {string}
 */
export function resolveTrainerClubMembershipErrorKey(reason) {
  const r = __asStr(reason);
  if (r === "membership-required") return "trainerstunden.trainer.clubs.error.membershipRequired";
  if (r === "primary-required") return "trainerstunden.trainer.clubs.error.primaryRequired";
  if (r === "forbidden") return "trainerstunden.trainer.clubs.error.forbidden";
  if (r === "free-training-trainer") return "trainerstunden.trainer.clubs.error.freeTraining";
  if (r === "rpc-missing") return "trainerstunden.trainer.clubs.error.schemaPending";
  return "trainerstunden.trainer.clubs.error.saveFailed";
}

/**
 * @param {TrainerClubMembership[]} memberships
 * @param {(key: string, params?: object) => string} t
 * @returns {string}
 */
export function formatTrainerClubMembershipsSummary(memberships, t) {
  const list = sortTrainerClubMemberships(memberships);
  if (!list.length) return t("trainerstunden.trainer.clubs.noneAssigned");
  return list
    .map((m) => {
      const label = m.city ? `${m.clubName} (${m.city})` : m.clubName;
      return m.isPrimary ? `${label} (${t("trainerstunden.trainer.clubs.primaryShort")})` : label;
    })
    .join(" · ");
}
